import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { firestore } from "../FirebaseConfig";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faStore,
  faList,
  faCoins,
  faPercent,
  faCalendarAlt,
  faStar,
  faCrown,
  faFire,
  faRocket,
  faSpinner,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/BusinessListings.css";
import {
  createBusinessListing,
  deleteBusinessListing,
  fetchBusinessListings,
} from "../utils/businessListings";
import { trackEvent } from "../utils/analytics";
import {
  CATEGORIES,
  ISLANDS,
  LISTING_TAGS,
  PASS_TIER_KEYS,
  PASS_TIER_LABELS,
  resolveCategoryKey,
  resolveIslandKey,
  getCategoryLabel,
} from "../constants/categories";
import { SkeletonVariants } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { useToast } from "../components/ui/Toast";
import { useConfirm } from "../components/ui/ConfirmDialog";
import { ErrorMessage, useErrorHandler } from "../components/ui/ErrorMessage";
import { SEO, PAGE_META, CinematicHero, GlassSection, R5Tabs, R5Card, R5FilterRow, R5Pagination } from "../components/ui";
import { SmartImage } from "../components/OptimizedImage";
import RedemptionResultModal from "../components/RedemptionResultModal";
import TokenSpendIndicator from "../components/TokenSpendIndicator";
import { ImageGallery } from "../components/ListingDetails";

const functions = getFunctions(undefined, "us-central1");

// Maps promotion type → badge label
const PROMO_BADGE = {
  premium:   { label: "⚓ Premium",  icon: faCrown  },
  featured:  { label: "★ Featured",  icon: faStar   },
  spotlight: { label: "Spotlight",   icon: faFire   },
  boost:     { label: "Trending",    icon: faRocket },
};

function isActivePromotion(listing) {
  return (
    listing.promotion?.isActive &&
    listing.promotion?.expiresAt &&
    new Date(listing.promotion.expiresAt) > new Date()
  );
}

// A listing is shown to the public only after an admin has approved it
// (status === "active"). Listings without a status field fall back to hidden
// so legacy/incomplete docs don't leak into the directory.
function isListingApproved(listing) {
  return listing?.status === "active";
}

const PLACEHOLDER_LISTING_IMAGE = "/assets/placeholder-image.png";

const getListingImage = (listing) => {
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    return listing.images[0];
  }

  if (typeof listing?.image === "string" && listing.image.trim()) {
    return listing.image;
  }

  return PLACEHOLDER_LISTING_IMAGE;
};

// TEMP DIAGNOSTIC (Workstream A0): expose the image-related fields from each
// fetched listing so we can confirm whether the data actually contains the
// expected `images` array / `profileImageUrl` / `thumbnailUrl`.
function debugLogListingImages(label, listings) {
  try {
    const summary = (Array.isArray(listings) ? listings : []).map((l) => ({
      id: l?.id,
      name: l?.name,
      imagesLen: Array.isArray(l?.images) ? l.images.length : null,
      firstImage: Array.isArray(l?.images) ? l.images[0] : null,
      profileImageUrl: l?.profileImageUrl,
      thumbnailUrl: l?.thumbnailUrl,
      legacyImage: l?.image,
    }));
    // eslint-disable-next-line no-console
    console.log(`[BusinessListings] ${label}`, summary);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[BusinessListings] debug log failed', e);
  }
}

const handleListingImageError = (event) => {
  if (event.currentTarget.src.includes(PLACEHOLDER_LISTING_IMAGE)) {
    return;
  }

  event.currentTarget.src = PLACEHOLDER_LISTING_IMAGE;
};

function BusinessListings() {
  const { user } = useAuth();
  const { isBusinessOwner } = usePlans();
  const location = useLocation(); // Get location object for query params
  const navigate = useNavigate(); // To potentially clear params after use
  const { listingId: routeListingId } = useParams();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { error, setError, clearError } = useErrorHandler();
  
  // Determine initial tab based on query parameter
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "browse";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

  // Filter / pagination state for the R5 filter row (M1 design).
  // Search filters by name/type/location; island/sort selects render but
  // are visual placeholders matching the mock until backend filters land.
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  // V2 directory filters (Phase 4). Empty string means "any".
  const [categoryFilter, setCategoryFilter] = useState("");
  const [islandFilter, setIslandFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [passTierFilter, setPassTierFilter] = useState("");
  const [earnsDoubloonsOnly, setEarnsDoubloonsOnly] = useState(false);
  const [bookableOnly, setBookableOnly] = useState(false);
  const PAGE_SIZE = 8;

  // Redeemable items for the selected listing
  const [redeemableItems, setRedeemableItems] = useState([]);
  const [redemptionLoading, setRedemptionLoading] = useState(false);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [showRedemptionResult, setShowRedemptionResult] = useState(false);

  // Check if user has business subscription
  const hasBusinessSubscription = isBusinessOwner();

  const loadListings = async () => {
    setLoading(true);
    clearError();
    try {
      const allListings = await fetchBusinessListings();
      debugLogListingImages('fetched listings', allListings);
      setListings(allListings);

      if (user && user.isBusinessOwner) {
        const myListings = allListings.filter(
          (listing) => listing.ownerId === user.uid
        );
        setMyListings(myListings);
      }
    } catch (err) {
      console.error("Error fetching listings:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Update activeTab when query parameter changes (for external links)
  useEffect(() => {
    const tabQuery = new URLSearchParams(location.search).get("tab");
    if (tabQuery && tabQuery !== activeTab) {
      setActiveTab(tabQuery);
    }
  }, [location.search, activeTab]);

  // Load redeemable items for the selected listing
  useEffect(() => {
    if (!selectedListing?.id) {
      setRedeemableItems([]);
      return;
    }

    const q = query(
      collection(firestore, "businessListings", selectedListing.id, "redeemableItems"),
      where("enabled", "==", true),
      orderBy("displayOrder", "asc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setRedeemableItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error("Error loading redeemable items:", err);
      setRedeemableItems([]);
    });

    return () => unsub();
  }, [selectedListing?.id]);

  // Handle listing selection — fires analytics view event
  const handleSelectListing = (listing) => {
    setSelectedListing(listing);
    trackEvent("listing_card_click", {
      refId: listing?.id,
      refType: "businessListing",
    });
    // Non-blocking: track the view server-side
    httpsCallable(functions, "trackListingView")({ listingId: listing.id }).catch(() => {});
  };

  // When the URL includes /business-listings/:listingId (e.g. from the
  // homepage featured grid), open that listing's detail view once listings
  // have loaded. Falls back to the list if the id isn't found.
  useEffect(() => {
    if (!routeListingId) return;
    if (selectedListing?.id === routeListingId) return;
    if (loading || listings.length === 0) return;
    const match = listings.find((l) => l.id === routeListingId);
    if (match) {
      setSelectedListing(match);
      httpsCallable(functions, "trackListingView")({ listingId: match.id }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeListingId, loading, listings]);

  // Reset listing selection
  const handleBackToList = () => {
    setSelectedListing(null);
  };

  const handleAddListing = async (newListing) => {
    try {
      const createdListing = await createBusinessListing(newListing);
      setMyListings([...myListings, createdListing]);
      showToast("Ahoy! Listing created successfully!", "success");
    } catch (err) {
      console.error("Error adding listing:", err);
      showToast("Failed to create listing. Please try again.", "error");
    }
  };

  const handleDeleteListing = async (id) => {
    const confirmed = await confirm({
      title: "Delete Listing",
      message: "Are ye sure ye want to scuttle this listing? This cannot be undone!",
      confirmText: "Delete",
      cancelText: "Keep It",
      variant: "danger"
    });

    if (!confirmed) return;

    try {
      await deleteBusinessListing(id);
      setMyListings(myListings.filter((listing) => listing.id !== id));
      showToast("Listing has been removed from the fleet.", "success");
    } catch (err) {
      console.error("Error deleting listing:", err);
      showToast("Failed to delete listing. Please try again.", "error");
    }
  };

  const handleEditListingPage = (listing) => {
    navigate(`/edit-business-listing/${listing.id}`);
  };

  // Handle traveller self-service redemption
  const handleRedeemItem = async (item) => {
    if (!user || !selectedListing?.id) return;

    setRedemptionLoading(true);
    setRedemptionResult(null);

    try {
      const fn = httpsCallable(functions, "createPendingRedemption");
      const res = await fn({
        businessId: selectedListing.id,
        itemId: item.id,
      });

      const { validationCode, requiresStaffConfirmation } = res.data;

      setRedemptionResult({
        ok: true,
        itemName: item.name,
        itemCost: item.tokenCost,
        validationCode,
        isPending: !!requiresStaffConfirmation,
        message: requiresStaffConfirmation
          ? "Your redemption is pending staff confirmation."
          : "Your redemption is confirmed!",
      });
      setShowRedemptionResult(true);
    } catch (err) {
      const friendlyMessage =
        err?.code === "failed-precondition"
          ? err.message || "You don't have enough Doubloons or have reached the redemption limit."
          : err?.code === "resource-exhausted"
          ? "This item's inventory is depleted."
          : err?.code === "unauthenticated"
          ? "You must be signed in to redeem."
          : err?.message || "Redemption failed. Please try again.";

      setRedemptionResult({
        ok: false,
        message: friendlyMessage,
      });
      setShowRedemptionResult(true);
    } finally {
      setRedemptionLoading(false);
    }
  };

  // Render listing detail view
  const renderListingDetail = () => {
    if (!selectedListing) return null;

    return (
      <div className="listing-detail">
        <div className="listing-header">
          <span className="listing-type">{selectedListing.type}</span>
        </div>

        <div className="listing-content">
          <div className="listing-image">
            {Array.isArray(selectedListing.images) && selectedListing.images.length > 0 ? (
              <ImageGallery
                images={selectedListing.images}
                alt={selectedListing.name || "Business image"}
              />
            ) : (
              <img
                src={getListingImage(selectedListing)}
                alt={selectedListing.name}
                onError={handleListingImageError}
              />
            )}
          </div>

          <div className="listing-info">
            <p className="listing-description">{selectedListing.description}</p>

            <div className="listing-details">
              <p>
                <strong>Location:</strong> {selectedListing.location}
              </p>
              <p>
                <strong>Contact:</strong> {selectedListing.contact}
              </p>
              <p>
                <strong>Phone:</strong> {selectedListing.phone}
              </p>
            </div>

            {/* Discounts Section */}
            <div className="listing-section">
              <h3>
                <FontAwesomeIcon icon={faPercent} /> Discounts & Offers
              </h3>

              {selectedListing.discounts &&
              selectedListing.discounts.length > 0 ? (
                <ul className="discounts-list">
                  {selectedListing.discounts.map((discount) => (
                    <li key={discount.id} className="discount-item">
                      <div className="discount-name">{discount.title}</div>
                      <div className="discount-description">
                        {discount.description}
                      </div>
                      <div className="discount-cost">
                        {discount.tokenCost} Doubloons
                      </div>
                      <button className="claim-discount-btn">
                        Claim Offer
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-items">No current discounts or offers.</p>
              )}
            </div>

            {/* Redeem Section — Traveller self-service redemptions */}
            {redeemableItems.length > 0 && (
              <div className="listing-section">
                <div className="listing-section__header">
                  <h3>
                    <FontAwesomeIcon icon={faCoins} /> Redeem with Doubloons
                  </h3>
                  <TokenSpendIndicator
                    userId={user?.uid}
                    src="business-listings-redeem"
                  />
                </div>

                <ul className="redeemables-list">
                  {redeemableItems.map((item) => {
                    const balance = user?.tokenBalance || 0;
                    const canAfford = balance >= item.tokenCost;
                    const exceedsLimit =
                      typeof item.maxRedemptionsPerUser === "number" &&
                      (user?.redemptionCount?.[item.id] || 0) >= item.maxRedemptionsPerUser;

                    return (
                      <li
                        key={item.id}
                        className={`redeemable-item${!canAfford || exceedsLimit ? " redeemable-item--unavailable" : ""}`}
                      >
                        <div className="redeemable-name">{item.name}</div>
                        {item.description && (
                          <div className="redeemable-description">{item.description}</div>
                        )}
                        <div className="redeemable-meta">
                          <span className="redeemable-cost">
                            <FontAwesomeIcon icon={faCoins} /> {item.tokenCost}
                          </span>
                          {item.requiresStaffConfirmation && (
                            <span className="redeemable-badge">Requires confirmation</span>
                          )}
                        </div>
                        <button
                          className="redeem-btn"
                          disabled={redemptionLoading || !canAfford || exceedsLimit}
                          onClick={() => handleRedeemItem(item)}
                          title={
                            !canAfford
                              ? `You need ${item.tokenCost - balance} more Doubloon${item.tokenCost - balance !== 1 ? "s" : ""}`
                              : exceedsLimit
                              ? "You've reached the limit for this item"
                              : `Redeem "${item.name}"`
                          }
                        >
                          {redemptionLoading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Processing…
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCoins} /> Redeem
                            </>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render grid of listings as R5Cards (R5-M1 "Browse the Cove" pattern).
  const renderListingsGrid = (listingsData) => (
    <div className="r5-grid--browse">
      {listingsData.map((listing) => {
        const promoted = isActivePromotion(listing);
        const promo    = promoted ? PROMO_BADGE[listing.promotion.type] : null;
        const canonical = resolveCategoryKey(listing.category || listing.type);
        const categoryLabel = canonical ? getCategoryLabel(canonical) : (listing.category || listing.type);
        return (
          <R5Card
            key={listing.id}
            onClick={() => handleSelectListing(listing)}
            featured={promoted}
            badge={promo?.label}
            imageSrc={getListingImage(listing)}
            imageAlt={listing.name || ''}
            category={categoryLabel}
            title={listing.name}
            blurb={listing.location}
            meta={
              <>
                <span>
                  {listing.discounts?.length > 0 && (
                    <span className="badge discount-badge" style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faPercent} /> Offers
                    </span>
                  )}
                  {listing.earnsDoubloons && (
                    <span className="badge doubloons-badge" style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faCoins} /> Doubloons
                    </span>
                  )}
                  {listing.bookingEnabled && (
                    <span className="badge booking-badge">
                      <FontAwesomeIcon icon={faCalendarCheck} /> Bookable
                    </span>
                  )}
                </span>
                <strong>View →</strong>
              </>
            }
          />
        );
      })}
    </div>
  );

  // Render list of listings
  const renderListingsList = (listingsData) => {
    return (
      <div className="listings-list">
        {listingsData.map((listing) => (
          <div
            key={listing.id}
            className="listing-row"
            onClick={() => handleSelectListing(listing)}
          >
            <div className="listing-row-image">
              <SmartImage
                src={getListingImage(listing)}
                alt={listing.name || ''}
                onError={handleListingImageError}
                className="listing-row-image__inner"
                objectFit="cover"
              />
            </div>
            <div className="listing-row-content">
              <h3>{listing.name}</h3>
              <p className="listing-type">{listing.type}</p>
              <p className="listing-description-short">
                {listing.description.length > 100
                  ? `${listing.description.substring(0, 100)}...`
                  : listing.description}
              </p>
            </div>
            <div className="listing-row-badges">
              {isActivePromotion(listing) && (
                <span className="badge promo-badge">
                  <FontAwesomeIcon icon={PROMO_BADGE[listing.promotion.type]?.icon || faStar} />{" "}
                  {PROMO_BADGE[listing.promotion.type]?.label || "Featured"}
                </span>
              )}
              {listing.discounts?.length > 0 && (
                <span className="badge discount-badge">
                  <FontAwesomeIcon icon={faPercent} /> {listing.discounts.length}
                </span>
              )}
              {listing.earnsDoubloons && (
                <span className="badge doubloons-badge">
                  <FontAwesomeIcon icon={faCoins} /> Doubloons
                </span>
              )}
              {listing.bookingEnabled && (
                <span className="badge booking-badge">
                  <FontAwesomeIcon icon={faCalendarCheck} /> Bookable
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render my listings manager (for business users)
  const renderMyListingsManager = () => {
    return (
      <div className="my-listings">
        <div className="my-listings-header">
          <h2>My Business Listings</h2>
          <button
            className="add-listing-btn"
            onClick={() => {
              const newListing = {
                name: "New Listing",
                description: "Description",
                images: [],
              }; // Example data
              handleAddListing(newListing);
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add New Listing
          </button>
        </div>

        {myListings.length === 0 ? (
          <EmptyState
            icon={faStore}
            title="No Listings Yet"
            message="Ye haven't created any business listings yet. Start yer voyage by adding yer first business!"
            actionLabel="Create Your First Listing"
            onAction={() => {
              const newListing = {
                name: "First Listing",
                description: "Description",
                images: [],
              };
              handleAddListing(newListing);
            }}
          />
        ) : (
          <div className="my-listings-list">
            {myListings.map((listing) => (
              <div key={listing.id} className="my-listing-item">
                <div className="my-listing-image">
                  <img
                    src={getListingImage(listing)}
                    alt={listing.name}
                    onError={handleListingImageError}
                  />
                </div>

                <div className="my-listing-content">
                  <h3>{listing.name}</h3>
                  <p className="listing-type">{listing.type}</p>
                  <div className="listing-status">
                    <span className={`status-badge ${listing.status}`}>
                      {listing.status.charAt(0).toUpperCase() +
                        listing.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="my-listing-actions">
                  <button
                    className="edit-listing-btn"
                    onClick={() => handleEditListingPage(listing)}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>

                  <button
                    className="manage-tokens-btn"
                    onClick={() => console.log("Manage Tokens for", listing.id)}
                  >
                    Manage Doubloons
                  </button>

                  <button
                    className="delete-listing-btn"
                    onClick={() => handleDeleteListing(listing.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Create a function to render the Events Tab Content
  const renderEvents = () => {
    // Only listings explicitly marked as events appear on this tab.
    const events = listings.filter(
      (item) => isListingApproved(item) && item.isEvent
    );

    if (events.length === 0) {
      return (
        <EmptyState
          icon={faCalendarAlt}
          title="No Events"
          message="There be no special events at the moment. Check back soon, matey!"
        />
      );
    }

    // Render events with R5Card in the M1 featured grid — first card featured.
    return (
      <div className="r5-grid--featured">
        {events.map((item, idx) => (
          <R5Card
            key={item.id}
            onClick={() => handleSelectListing(item)}
            featured={idx === 0}
            badge={idx === 0 ? "Featured Tonight" : undefined}
            imageSrc={getListingImage(item)}
            imageAlt={item.name || ''}
            priceOverlay={
              item.tokenCost ? (
                <>
                  <FontAwesomeIcon icon={faCoins} /> {item.tokenCost} Doubloons
                </>
              ) : null
            }
            category={
              item.date
                ? new Date(item.date).toLocaleDateString()
                : item.type || "Event"
            }
            title={item.name}
            blurb={item.description || item.location}
            meta={
              <>
                <span>{item.location}</span>
                <strong>View Event →</strong>
              </>
            }
          />
        ))}
      </div>
    );
  };

  // Main render
  const browseTabs = [
    { key: "browse", label: "Browse Businesses", icon: <FontAwesomeIcon icon={faStore} /> },
    { key: "events", label: "Events", icon: <FontAwesomeIcon icon={faCalendarAlt} /> },
    ...(hasBusinessSubscription
      ? [{ key: "manage", label: "Manage My Listings", icon: <FontAwesomeIcon icon={faEdit} /> }]
      : []),
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedListing(null);
  };

  const showViewSwitcher =
    (activeTab === "browse" || activeTab === "events") && !selectedListing;

  const viewSwitcher = showViewSwitcher ? (
    <div className="view-mode-switcher">
      <button
        className={`view-mode-btn ${viewMode === "grid" ? "active" : ""}`}
        onClick={() => setViewMode("grid")}
      >
        Grid
      </button>
      <button
        className={`view-mode-btn ${viewMode === "list" ? "active" : ""}`}
        onClick={() => setViewMode("list")}
      >
        <FontAwesomeIcon icon={faList} /> List
      </button>
    </div>
  ) : null;

  const sectionActions = selectedListing ? (
    <button className="back-button" onClick={handleBackToList}>
      ← Back to Listings
    </button>
  ) : (
    viewSwitcher
  );

  const sectionTitle =
    activeTab === "events"
      ? "Events"
      : activeTab === "manage"
      ? "My Listings"
      : "Browse the Cove";

  return (
    <>
      <SEO 
        title={PAGE_META.listings.title}
        description={PAGE_META.listings.description}
        url="/business-listings"
      />
      <CinematicHero
        eyebrow="· Discover ·"
        title="Island Businesses"
        dropCap="I"
        lede="Member-owned shops, eateries, and adventures charted across the USVI — every berth lit, every crew accounted for."
      />
      <R5Tabs
        tabs={browseTabs}
        activeKey={activeTab}
        onChange={handleTabChange}
      />
      {showViewSwitcher && (
        <R5FilterRow
          search={{
            value: search,
            onChange: (v) => { setSearch(v); setPage(1); },
            placeholder: "Search businesses…",
          }}
          selects={[
            {
              value: islandFilter,
              onChange: (v) => { setIslandFilter(v); setPage(1); },
              options: ISLANDS.map((i) => ({ value: i.key, label: i.label })),
              placeholder: "All Islands",
            },
            {
              value: categoryFilter,
              onChange: (v) => { setCategoryFilter(v); setPage(1); },
              options: CATEGORIES.map((c) => ({ value: c.key, label: c.label })),
              placeholder: "All Categories",
            },
            {
              value: tagFilter,
              onChange: (v) => { setTagFilter(v); setPage(1); },
              options: LISTING_TAGS.map((t) => ({ value: t.key, label: t.label })),
              placeholder: "All Vibes",
            },
            {
              value: passTierFilter,
              onChange: (v) => { setPassTierFilter(v); setPage(1); },
              options: Object.values(PASS_TIER_KEYS).map((k) => ({ value: k, label: PASS_TIER_LABELS[k] })),
              placeholder: "Any Pass Tier",
            },
          ]}
          chips={[
            {
              label: "★ Featured",
              active: showFeaturedOnly,
              onClick: () => { setShowFeaturedOnly((v) => !v); setPage(1); },
            },
            {
              label: "🪙 Earns Doubloons",
              active: earnsDoubloonsOnly,
              onClick: () => { setEarnsDoubloonsOnly((v) => !v); setPage(1); },
            },
            {
              label: "📅 Bookable",
              active: bookableOnly,
              onClick: () => { setBookableOnly((v) => !v); setPage(1); },
            },
          ]}
        />
      )}
      <GlassSection
        className="business-listings-page"
        title={selectedListing ? selectedListing.name : sectionTitle}
        actions={sectionActions}
      >
      <div className="page-content">
        {error ? (
          <ErrorMessage 
            error={error} 
            onRetry={loadListings}
            showHomeButton={true}
          />
        ) : loading ? (
          <div className="listings-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonVariants.BusinessListing key={i} />
            ))}
          </div>
        ) : (
          <>
            {(() => {
              if (selectedListing) return renderListingDetail();
              if (activeTab === "events") return renderEvents();
              if (activeTab === "manage") return renderMyListingsManager();

              // Browse: apply search + featured filter, sort promoted first, then paginate.
              // Only show approved (active) listings to the public.
              const browse = listings.filter((l) => isListingApproved(l) && !l.isEvent);
              if (browse.length === 0) {
                return (
                  <EmptyState
                    icon={faStore}
                    title="No Businesses Yet"
                    message="The harbor is empty! Be the first to add yer business to the guild."
                    actionLabel={hasBusinessSubscription ? "Add Your Business" : null}
                    onAction={hasBusinessSubscription ? () => setActiveTab("manage") : null}
                  />
                );
              }
              const q = search.trim().toLowerCase();
              const searched = q
                ? browse.filter((l) =>
                    [l.name, l.type, l.category, l.location]
                      .filter(Boolean)
                      .some((v) => String(v).toLowerCase().includes(q))
                  )
                : browse;

              // V2 filters: category, island, tag, pass tier, earns Doubloons, bookable.
              // All checks are forgiving so legacy listings without the new fields
              // still show up unless the user explicitly narrows by that facet.
              const facetFiltered = searched.filter((l) => {
                if (categoryFilter) {
                  const key = resolveCategoryKey(l.category || l.type);
                  if (key !== categoryFilter) return false;
                }
                if (islandFilter) {
                  const key = resolveIslandKey(l.island || l.location);
                  if (key !== islandFilter) return false;
                }
                if (tagFilter) {
                  const tags = Array.isArray(l.tags) ? l.tags : [];
                  if (!tags.includes(tagFilter)) return false;
                }
                if (passTierFilter) {
                  const eligible = Array.isArray(l.eligiblePassTiers) ? l.eligiblePassTiers : null;
                  // If a listing hasn't set eligibility, treat it as open to all tiers.
                  if (eligible && !eligible.includes(passTierFilter)) return false;
                }
                if (earnsDoubloonsOnly && !l.earnsDoubloons) return false;
                if (bookableOnly && !l.bookingEnabled) return false;
                return true;
              });

              // Apply "Featured only" chip filter, then sort promoted listings first
              const filtered = (showFeaturedOnly ? facetFiltered.filter(isActivePromotion) : facetFiltered)
                .slice()
                .sort((a, b) => {
                  const aP = isActivePromotion(a) ? 1 : 0;
                  const bP = isActivePromotion(b) ? 1 : 0;
                  return bP - aP;
                });
              const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
              const safePage = Math.min(page, pageCount);
              const slice = filtered.slice(
                (safePage - 1) * PAGE_SIZE,
                safePage * PAGE_SIZE
              );
              return (
                <>
                  {viewMode === "grid"
                    ? renderListingsGrid(slice)
                    : renderListingsList(slice)}
                  <R5Pagination
                    page={safePage}
                    pageCount={pageCount}
                    onPageChange={setPage}
                  />
                </>
              );
            })()}
          </>
        )}
      </div>
      </GlassSection>

      <RedemptionResultModal
        isOpen={showRedemptionResult}
        onClose={() => {
          setShowRedemptionResult(false);
          setRedemptionResult(null);
        }}
        result={redemptionResult}
        loading={redemptionLoading}
      />
    </>
  );
}

export default BusinessListings;
